import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import Icon from "../AppIcon";

import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../utils/permissions";

const Sidebar = ({
  isCollapsed = false,
  isOpen = false,
  onClose,
  onCollapseToggle,
}) => {
  const location = useLocation();
  const { user } = useAuth();
  const [statisticsOpen, setStatisticsOpen] = useState(false);

  // ✅ IMPORTANT: access keys MUST match moduleLabels keys in Users page rights
  const navigationItems = [
    { label: "Operations Hub", path: "/dashboard", icon: "Activity", access: "dashboard", badge: "Live" },
    { label: "Workspace", path: "/orders-management", icon: "ShoppingBag", access: "orders" },
    { label: "Online Booking", path: "/online-orders", icon: "Smartphone", access: "onlineOrders" },
    { label: "Lab Orders", path: "/lab-orders", icon: "FlaskConical", access: "labOrders" },
    { label: "Lab Report", path: "/reports", icon: "FileText", access: "labReport" },
    { label: "Payments", path: "/payments", icon: "IndianRupee", access: "payments" },
    { label: "Reg.Patients", path: "/patient-management", icon: "UserCheck", access: "patient" },
    { label: "Enquiry", path: "/enquiry", icon: "MessageCircle", access: "enquiry" },
    { label: "Cart-Activity", path: "/cart-details", icon: "ShoppingCart", access: "cart" },
    { label: "Prescriptions", path: "/prescriptions", icon: "ClipboardList", access: "prescriptions" },
    { label: "Master Data", path: "/master-management", icon: "Database", access: "master" },
    { label: "Vendors", path: "/vendor-performance-hub", icon: "Truck", access: "vendor" },
    { label: "Report Master", path: "/packages-management", icon: "Package", access: "packages" },
    { label: "Users", path: "/user-management", icon: "UserCog", access: "user" },
    { label: "Banners", path: "/banners", icon: "Image", access: "banners" },
    { label: "B2B Centers", path: "/centers", icon: "Hospital", access: "centers" },
    { label: "Coupons", path: "/coupons", icon: "Ticket", access: "coupons" },
    { label: "Notifications", path: "/push-notifications", icon: "Bell", access: "notification" },
  ];

  // Statistics sub-items (moved from standalone sidebar + master data)
  const statisticsItems = [
    { label: "Ref Centers", path: "/statistics/ref-centers", icon: "Building2" },
    { label: "Ref Doctors", path: "/statistics/ref-doctors", icon: "Stethoscope" },
    { label: "Turn Around Time", path: "/TAT", icon: "Clock" },
    { label: "Reports", path: "/reports-page", icon: "BarChart3" },
  ];

  const isActive = (path) => location?.pathname === path;

  const isStatsItemActive = (path) => location?.pathname === path;

  const isStatisticsGroupActive = statisticsItems.some((item) =>
    isStatsItemActive(item.path)
  );

  // Auto-expand Statistics group when a sub-item is active
  useEffect(() => {
    if (isStatisticsGroupActive) setStatisticsOpen(true);
  }, [location.pathname]);

  const handleItemClick = () => {
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed top-16 left-0 bottom-0 z-40 bg-white border-r border-gray-200 shadow-md
          transition-all duration-300 ease-out
          ${isCollapsed ? "w-16" : "w-56"}
          ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          flex flex-col
        `}
      >
        {/* HEADER */}
        <div
          className={`flex items-center ${
            isCollapsed ? "justify-center" : "justify-between"
          } p-4 border-b bg-gray-50 relative`}
        >
          {!isCollapsed ? (
            <div className="flex items-center space-x-3">
              <div>
                <h1 className="text-lg font-bold text-gray-900">Health care</h1>
                <p className="text-xs text-gray-500 -mt-1">Management Portal</p>
              </div>
            </div>
          ) : (
           <></>
          )}

          {/* ✅ Toggle button visible in BOTH states */}
          {typeof onCollapseToggle === "function" && (
            <button
              onClick={onCollapseToggle}
              className={`absolute ${
                isCollapsed ? "-right-3" : "-right-3"
              } top-6 w-7 h-7 bg-blue-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-600 border-2 border-white transition`}
              title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              <Icon name={isCollapsed ? "ChevronsRight" : "ChevronsLeft"} size={14} />
            </button>
          )}
        </div>

        {/* NAV */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {/* Flat nav items */}
          {navigationItems
            .filter((item) => canAccess(user, item.access))
            .map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={handleItemClick}
                  className={`
                    group relative flex items-center cursor-pointer rounded-lg transition-all
                    ${isCollapsed ? "justify-center px-2 py-3" : "px-3 py-2.5"}
                    ${
                      active
                        ? "bg-blue-50 text-blue-700 border-l-2 border-blue-500 shadow-sm"
                        : "text-gray-600 border-l-2 border-transparent hover:bg-gray-50 hover:text-gray-900"
                    }
                  `}
                  title={isCollapsed ? item.label : ""}
                >
                  <Icon
                    name={item.icon}
                    size={20}
                    className={`${active ? "text-blue-500" : "text-gray-400"} group-hover:text-blue-500`}
                  />
                  {!isCollapsed && (
                    <div className="ml-3 flex items-center justify-between w-full">
                      <span className="text-sm font-medium">{item.label}</span>
                      {item.badge && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
                          {item.badge}
                        </span>
                      )}
                    </div>
                  )}
                </Link>
              );
            })}

          {/* ── Statistics Group ── */}
          {canAccess(user, "statistics") && (
            <div>
              {/* Group header button */}
              <button
                onClick={() => setStatisticsOpen((prev) => !prev)}
                className={`
                  group relative flex items-center cursor-pointer rounded-lg transition-all w-full
                  ${isCollapsed ? "justify-center px-2 py-3" : "px-3 py-2.5"}
                  ${
                    isStatisticsGroupActive
                      ? "bg-blue-50 text-blue-700 border-l-2 border-blue-500 shadow-sm"
                      : "text-gray-600 border-l-2 border-transparent hover:bg-gray-50 hover:text-gray-900"
                  }
                `}
                title={isCollapsed ? "Statistics" : ""}
              >
                <Icon
                  name="PieChart"
                  size={20}
                  className={`${isStatisticsGroupActive ? "text-blue-500" : "text-gray-400"} group-hover:text-blue-500`}
                />
                {!isCollapsed && (
                  <div className="ml-3 flex items-center justify-between w-full">
                    <span className="text-sm font-medium">Statistics</span>
                    <Icon
                      name={statisticsOpen ? "ChevronDown" : "ChevronRight"}
                      size={14}
                      className="text-gray-400"
                    />
                  </div>
                )}
              </button>

              {/* Sub-items — visible when expanded (or always when collapsed for tooltip) */}
              {(statisticsOpen || isCollapsed) && (
                <div className={`${!isCollapsed ? "ml-3 border-l border-gray-200 pl-2 mt-1 space-y-0.5" : "space-y-0.5"}`}>
                  {statisticsItems.map((item) => {
                    const active = isStatsItemActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={handleItemClick}
                        className={`
                          group flex items-center cursor-pointer rounded-lg transition-all
                          ${isCollapsed ? "justify-center px-2 py-3" : "px-3 py-2"}
                          ${
                            active
                              ? "bg-blue-50 text-blue-700 border-l-2 border-blue-500"
                              : "text-gray-500 border-l-2 border-transparent hover:bg-gray-50 hover:text-gray-800"
                          }
                        `}
                        title={isCollapsed ? item.label : ""}
                      >
                        <Icon
                          name={item.icon}
                          size={16}
                          className={`${active ? "text-blue-500" : "text-gray-400"} group-hover:text-blue-500`}
                        />
                        {!isCollapsed && (
                          <span className="ml-2.5 text-xs font-medium">{item.label}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* FOOTER */}
        <div className="p-3 border-t bg-gray-50">
          {!isCollapsed ? (
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span>Online</span>
              </div>
              <span>
                {new Date().toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;